import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import { apiClient } from '../api/client';
import i18n from '../../i18n/config';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string | null;
  role: string;
  subscriptionTier: string;
  customDomain?: string | null;
  defaultResumeId?: string | null;
  preferredLanguage?: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = apiClient.getToken();
    if (token) {
      refreshUser();
    } else {
      setIsLoading(false);
    }
  }, []);

  const refreshUser = async () => {
    try {
      const userData = await apiClient.getCurrentUser();
      setUser(userData);

      // Apply user's preferred language if available
      if (userData.preferredLanguage && userData.preferredLanguage !== i18n.language) {
        console.log('Loading user preferred language:', userData.preferredLanguage);
        await i18n.changeLanguage(userData.preferredLanguage);

        // Update HTML dir attribute for RTL languages
        if (userData.preferredLanguage === 'ar') {
          document.documentElement.setAttribute('dir', 'rtl');
        } else {
          document.documentElement.setAttribute('dir', 'ltr');
        }

        // Update localStorage to match
        localStorage.setItem('language', userData.preferredLanguage);
      }
    } catch (error) {
      // Token invalid or expired
      apiClient.setToken(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    const response = await apiClient.login({ email, password });
    apiClient.setToken(response.access_token);
    apiClient.setRefreshToken(response.refresh_token);
    // Fetch fresh user data instead of using the response
    await refreshUser();
  };

  const register = async (data: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }) => {
    const response = await apiClient.register(data);
    apiClient.setToken(response.access_token);
    apiClient.setRefreshToken(response.refresh_token);
    // Fetch fresh user data instead of using the response
    await refreshUser();
  };

  const logout = async () => {
    await apiClient.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
